import i18n from 'i18next';
import { put, call, takeLatest, all, delay, take } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { groupDetailsActionTypes } from './Constants';
import { getTruncatedName } from '../../services/Helper';
import * as groupDetailsActions from './Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';

export default function* () {
	yield all([
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_DETAILS_REQUEST,
			handleGroupDetailsRequest,
		),
		yield takeLatest(groupDetailsActionTypes.GET_GROUP_LISTS_REQUEST, handleGroupListsRequest),
		yield takeLatest(groupDetailsActionTypes.GROUP_ADD_ITEMS_REQUEST, handleAddItemsRequest),
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_MEMBERS_REQUEST,
			handleGroupMembersRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.DELETE_USER_FROM_GROUP_REQUEST,
			handleDeleteUserRequest,
		),
		yield takeLatest(groupDetailsActionTypes.DECLINE_USER_REQUEST, handleDeclineUserRequest),
		yield takeLatest(
			groupDetailsActionTypes.REVOKE_USER_INVITE_REQUEST,
			handleRevokeUserInviteRequest,
		),
		yield takeLatest(groupDetailsActionTypes.ACCEPT_USER_REQUEST, handleAcceptUserRequest),
		yield takeLatest(
			groupDetailsActionTypes.CHECK_IF_USER_EXIST_REQUEST,
			handleCheckUserRequest,
		),
		yield takeLatest(groupDetailsActionTypes.INVITE_USERS_REQUEST, handleInviteUsersRequest),
		yield takeLatest(
			groupDetailsActionTypes.DELETE_LIST_FROM_GROUP_REQUEST,
			handleDeleteListFromGroupRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_LIST_MEMBERS_REQUEST,
			handleGetGroupListMembersRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_LIST_RESULTS_REQUEST,
			handleGetGroupListResultsRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_MEMBER_LISTS_REQUEST,
			handleGetGroupMemberListsRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_DIFFICULT_WORDS_REQUEST,
			handleGetDifficultWordsRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_QUESTIONS,
			handleGetGroupQuestionsRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_GROUP_QUESTION_ANSWERS,
			handleGetGroupQuestionAnswersRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.GET_INSTITUTE_LICENCES,
			handleGetInstituteLicencesRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.ADD_LICENCES_TO_GROUP,
			handleAddLicencesToGroupRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.REMOVE_INACTIVE_LICENSE,
			handleRemoveInactiveLicenseRequest,
		),
		yield takeLatest(
			groupDetailsActionTypes.PROMOTE_GROUP_USER_TO_MANAGER,
			handlePromoteGroupUserToManagerRequest,
		),
	]);
}

export function* handleGroupDetailsRequest(action) {
	try {
		const { groupId } = action.payload;
		const { data } = yield call(processRequest, `groups/${groupId}`);

		yield put(
			groupDetailsActions.getGroupDetailsSuccess({
				...data.group,
				breadcrumbs: data.breadcrumbs,
			}),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(groupDetailsActions.getGroupDetailsError());
	}
}

export function* handleGroupListsRequest(action) {
	try {
		const { groupId } = action.payload;
		const { data } = yield call(processRequest, `groups/${groupId}/practiceable_items`);

		yield put(groupDetailsActions.getGroupListsSuccess(data.practiceable_items));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(groupDetailsActions.getGroupListsError(e));
	}
}

export function* handleAddItemsRequest(action) {
	try {
		const { id, practiceableItems } = action.payload;

		yield call(processRequest, `groups/${id}/add_practiceable_items`, 'PATCH', {
			practiceable_items: practiceableItems,
		});

		yield put(groupDetailsActions.addItemsSuccess());
		yield put(groupDetailsActions.getGroupListsRequest(id));
		yield put(
			notificationActions.createNotification(
				i18n.t('group_details.add_exercise_to_group', { count: practiceableItems.length }),
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.addItemsError());
	}
}

export function* handleGroupMembersRequest(action) {
	try {
		const { groupId, range, type } = action.payload;
		const isMembersType = type === 'members';
		const { data } = yield call(
			processRequest,
			`groups/${groupId}/${isMembersType ? 'members' : 'users'}?range=${range}`,
		);

		const { group_users, users, inactive_licenses, users_with_expired_license } = data || {};

		yield put(
			groupDetailsActions.getGroupMembersSuccess(
				isMembersType ? group_users : users,
				inactive_licenses,
				users_with_expired_license,
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(groupDetailsActions.getGroupMembersError(e));
	}
}

export function* handleDeleteListFromGroupRequest({ payload }) {
	try {
		const { groupId, id, type } = payload.data;
		const requestPayload = {
			practiceable_item: {
				type,
				id,
			},
		};

		yield call(
			processRequest,
			`groups/${groupId}/remove_practiceable_item`,
			'POST',
			requestPayload,
		);

		yield put(groupDetailsActions.getGroupListsRequest(groupId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.deleteListFromGroupError());
	}
}

export function* handleDeleteUserRequest({ payload }) {
	try {
		const { groupId, groupUserId, callback } = payload || {};

		yield call(processRequest, `groups/${groupId}/users/${groupUserId}`, 'DELETE');

		yield put(groupDetailsActions.deleteUserFromGroupSuccess(groupUserId));

		if (callback) callback();
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.deleteUserFromGroupError());
	}
}

export function* handleDeclineUserRequest(action) {
	try {
		const { groupId, userId } = action.payload;

		yield call(processRequest, `groups/${groupId}/users/${userId}/reject_request`, 'DELETE');
		yield put(groupDetailsActions.declineUserSuccess(userId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.declineUserError());
	}
}

export function* handleRevokeUserInviteRequest(action) {
	try {
		const { groupId, groupUserId } = action.payload;

		yield call(
			processRequest,
			`groups/${groupId}/users/${groupUserId}/retract_invite`,
			'DELETE',
		);
		yield put(groupDetailsActions.revokeUserInviteSuccess(groupUserId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.revokeUserInviteError());
	}
}

export function* handleAcceptUserRequest(action) {
	try {
		const { groupId, userId, firstName, lastName } = action.payload;

		yield call(processRequest, `groups/${groupId}/users/${userId}/accept_request`, 'POST');
		yield put(
			notificationActions.createNotification(
				i18n.t('group_details.accept_user_notification', { firstName, lastName }),
			),
		);
		yield delay(3000);
		yield put(groupDetailsActions.acceptUserSuccess(userId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.acceptUserError());
	}
}

export function* handleCheckUserRequest({ payload }) {
	const { fieldValue, message, fieldIndex, fieldType } = payload;
	const isUserType = fieldType === 'username';
	const requestPayload = isUserType ? { user_name: fieldValue } : { email: fieldValue };
	const method = isUserType ? 'GET' : 'POST';

	try {
		yield call(processRequest, 'users/exists', method, requestPayload);
		yield put(groupDetailsActions.checkIfUserExistSuccess());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(groupDetailsActions.checkIfUserExistError(e, message, fieldIndex));
	}
}

export function* handleInviteUsersRequest({ payload }) {
	const { groupId, emails, usernames, isOnMembersPage } = payload;
	const requestPayload = { emails, user_names: usernames };

	try {
		const { type } = yield take([
			groupDetailsActionTypes.CHECK_IF_USER_EXIST_SUCCESS,
			groupDetailsActionTypes.CHECK_IF_USER_EXIST_ERROR,
		]);

		if (type === groupDetailsActionTypes.CHECK_IF_USER_EXIST_SUCCESS) {
			yield call(processRequest, `groups/${groupId}/invite_by_email`, 'POST', requestPayload);
			if (isOnMembersPage) {
				yield put(groupDetailsActions.getGroupMembersRequest(groupId));
			}
			yield put(
				notificationActions.createNotification(
					i18n.t('group_details.invite_user_notification'),
				),
			);
			yield put(groupDetailsActions.inviteUsersSuccess());
		} else {
			yield put(
				groupDetailsActions.inviteUsersError(
					i18n.t('group_details.invite_user_error_notification'),
				),
			);
		}
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking', true));
		yield put(groupDetailsActions.inviteUsersError());
	}
}

export function* handleGetGroupListMembersRequest({ payload }) {
	const { groupId, listId, type } = payload;

	try {
		const { data } = yield call(processRequest, `groups/${groupId}/users?${type}_id=${listId}`);

		yield put(groupDetailsActions.getGroupListMembersSuccess(data.users, listId, data.summary));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getGroupListMembersError(groupId, listId));
	}
}

export function* handleGetGroupListResultsRequest({ payload }) {
	const { groupId, userId, listId, range, type } = payload;
	const requestPayload = {
		user_id: userId,
		[`${type}_id`]: listId,
		range,
	};

	try {
		const { data } = yield call(
			processRequest,
			`groups/${groupId}/results`,
			'GET',
			requestPayload,
		);

		yield put(groupDetailsActions.getGroupListResultsSuccess(data, listId, userId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getGroupListResultsError(userId, listId));
	}
}

export function* handleGetGroupMemberListsRequest({ payload }) {
	const { groupId, userId, range } = payload;

	try {
		const { data } = yield call(
			processRequest,
			`groups/${groupId}/practiceable_items?user_id=${userId}&range=${range}`,
		);

		yield put(
			groupDetailsActions.getGroupMemberListsSuccess(
				data.practiceable_items,
				userId,
				data.summary,
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getGroupMemberListsError(userId));
	}
}

export function* handleGetDifficultWordsRequest({ payload }) {
	const { groupId, listId, range } = payload;

	try {
		const { data } = yield call(
			processRequest,
			`groups/${groupId}/difficult_words?list_id=${listId}&range=${range}`,
		);

		yield put(
			groupDetailsActions.getDifficultWordsSuccess(
				data.difficult_words,
				data.subject,
				listId,
			),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getDifficultWordsError(listId));
	}
}

export function* handleGetGroupQuestionsRequest({ payload }) {
	const { groupId, listId, type } = payload;

	try {
		const { data } = yield call(
			processRequest,
			`groups/${groupId}/questions?${type}_id=${listId}`,
		);

		yield put(groupDetailsActions.getGroupQuestionsSuccess(listId, data.questions));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getGroupQuestionsError(listId));
	}
}

export function* handleGetGroupQuestionAnswersRequest({ payload }) {
	const { groupId, questionId, listId, type } = payload;

	try {
		const { data } = yield call(
			processRequest,
			`groups/${groupId}/questions/${questionId}?${type}_id=${listId}`,
		);

		yield put(groupDetailsActions.getGroupQuestionAnswersSuccess(questionId, data.question));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getGroupQuestionAnswersError(questionId));
	}
}

export function* handleGetInstituteLicencesRequest({ payload }) {
	const { instituteId, groupId } = payload;

	try {
		const { data } = yield call(
			processRequest,
			`institutes/${instituteId}?group_id=${groupId}`,
		);
		yield put(groupDetailsActions.getInstituteLicencesSuccess(data?.institute?.licenses));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.getInstituteLicencesError());
	}
}

export function* handleAddLicencesToGroupRequest({ payload }) {
	const { groupId, licenseIds } = payload;

	try {
		yield call(processRequest, `groups/${groupId}/add_licenses`, 'PATCH', {
			license_ids: licenseIds,
		});
		yield put(groupDetailsActions.getGroupMembersRequest(groupId));
		yield put(groupDetailsActions.toggleGroupInviteModal({ open: false }));
		yield put(groupDetailsActions.addLicencesToGroupFinal());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.addLicencesToGroupFinal());
	}
}

export function* handleRemoveInactiveLicenseRequest({ payload }) {
	try {
		const { groupId, licenseId } = payload || {};

		yield call(processRequest, `groups/${groupId}/remove_inactive_license`, 'DELETE', {
			license_id: licenseId,
		});
		yield put(groupDetailsActions.removeInactiveLicenseSuccess(licenseId));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handlePromoteGroupUserToManagerRequest({ payload }) {
	try {
		const { groupId, user, callback } = payload || {};

		const { id, first_name } = user || {};

		yield call(processRequest, `groups/${groupId}/users/${id}/promote_to_manager`, 'POST');
		if (callback) callback();

		yield put(
			notificationActions.createNotification(
				i18n.t('group_details.set_manager_role_notification', {
					name: getTruncatedName(first_name),
				}),
			),
		);
		yield put(groupDetailsActions.promoteGroupUserToManagerFinal());
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(groupDetailsActions.promoteGroupUserToManagerFinal());
	}
}
